import { diContainer, serviceNames } from '../diContainer/diContainer';
import ShippingRepository from '../repositories/ShippingRepository';
import { ADDRESS_TYPE_ID, DeliveryOptions, FIELD_TYPE, NAVIGATION_ITEM_IDS } from '../constants/Constants';
import Config from '../domain/entities/Config';
import SelectNewShippingAddressUseCase from './SelectNewShippingAddressUseCase';
import Address from '../domain/entities/Address';
import BopisStoresRepository from '../repositories/BopisStoresRepository';
import AUPostCollectionsStoreRepository from '../repositories/AUPostCollectionsStoreRepository';
import UIState from '../stores/UIState';
import TransactionLoadHandler from './TransactionLoadHandler';

class SelectDeliveryOptionUseCase extends TransactionLoadHandler {
  private shippingRepository: ShippingRepository;

  private bopisStoresRepository: BopisStoresRepository;

  private auPostCollectionsStoreRepository: AUPostCollectionsStoreRepository;

  private config: Config;

  private deliveryOption: DeliveryOptions;

  private inEditMode: boolean;

  constructor(
    deliveryOption: DeliveryOptions,
    inEditMode = false,
  ) {
    super({
      isControlled: true,
    });
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.bopisStoresRepository = diContainer.get(serviceNames.bopisStoresRepository);
    this.auPostCollectionsStoreRepository = diContainer
      .get(serviceNames.auPostCollectionsStoreRepository);
    this.config = diContainer.get(serviceNames.config);

    this.deliveryOption = deliveryOption;
    this.inEditMode = inEditMode;
  }

  public run = async (): Promise<void> => {
    const {
      config,
      deliveryOption,
      transactionRepository,
      shippingRepository,
    } = this;

    const transaction = await transactionRepository.getTransaction();

    let { shippingAddress } = transaction;

    const resetNavigationOptions = [
      DeliveryOptions.CONVENIENCE_STORE_PICK_UP,
      DeliveryOptions.UPS_STORE_PICK_UP,
      DeliveryOptions.SAME_DAY_DELIVERY,
    ];

    if (resetNavigationOptions.includes(deliveryOption)) {
      const uiState: UIState = diContainer.get(serviceNames.uiState);
      uiState.updateItemActiveState(NAVIGATION_ITEM_IDS.paymentId, false);
    }

    // check if address is valid (e.g. exclude packstation from home address list)
    // if address type is not shipping or billing (address type is 1 or 2)
    // we need to reset transaction address
    const addressType = (
      transaction.shippingAddress?.attributes[FIELD_TYPE.ADDRESS_TYPE] || ADDRESS_TYPE_ID.SHIPPING
    ) as ADDRESS_TYPE_ID;
    if (
      deliveryOption === DeliveryOptions.HOME_DELIVERY
      && ![ADDRESS_TYPE_ID.SHIPPING, ADDRESS_TYPE_ID.BILLING].includes(addressType)
    ) {
      const { defaultShippingAddress } = this.shippingRepository;
      // if we can not find the address in user address book
      // then we need to unset the transaction address
      // and set the default or first address as the transaction address
      // to avoid using the packstation address (address_type = 4) as
      // selected shipping address
      if (defaultShippingAddress) {
        await shippingRepository.onSelectAddress(defaultShippingAddress.id);
        shippingRepository.formStore.setAddress(defaultShippingAddress);
        shippingAddress = defaultShippingAddress;
      } else {
        // if no address in address book, trigger new address action
        const useCase = new SelectNewShippingAddressUseCase();
        await useCase.execute();
        shippingAddress = new Address();
      }
    }
    const countryId = (shippingAddress?.isNewAddress || this.inEditMode)
      ? shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID]
      : shippingAddress?.attributes[FIELD_TYPE.COUNTRY_ID];
    // check default ship method from config and update ship method
    const shipMethod = transaction.getDefaultDeliveryMethod(this.deliveryOption, countryId || config.countryId);
    if (shipMethod && shipMethod !== transaction.selectedShippingMethodId) {
      await transactionRepository.setShippingMethod(shipMethod);
    }

    await this.handleTransactionUpdate();

    if (deliveryOption !== DeliveryOptions.CLICK_AND_COLLECT && deliveryOption !== DeliveryOptions.UPS_STORE_PICK_UP) {
      this.bopisStoresRepository.setIsBopisSelected(false);
    }

    if (deliveryOption === DeliveryOptions.UPS_STORE_PICK_UP) {
      this.bopisStoresRepository.setIsBopisSelected(true);
    }

    if (deliveryOption === DeliveryOptions.AU_POST_COLLECTIONS) {
      this.auPostCollectionsStoreRepository.setWidgetInitilized(false);
    }
  };
}

export default SelectDeliveryOptionUseCase;
